import { useState, useEffect, useMemo } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';

import { logError } from '@services/sentry-service';
import { CHECKOUT_STATE_PROJECT_ID, PAYMENT_METHOD } from '@utilities/constants';

export interface BrowserInfo {
  screenWidth: number;
  screenHeight: number;
  colorDepth: number;
  userAgent: string;
  timeZoneOffset: number;
  language: string;
}

const DEFAULT_BROWSER_INFO = {
  screenWidth: 0,
  screenHeight: 0,
  colorDepth: 1,
  userAgent: 'None',
  timeZoneOffset: 0,
  language: 'en-GB',
};

const BROWSER_INFO_REQUIRED_PAYMENTS = [
  PAYMENT_METHOD.CREDIT_CARD,
  PAYMENT_METHOD.BANCONTACT,
] as const;

export const useBrowserInfo = (): string => {
  const [browserInfo, setBrowserInfo] = useState<string>('');
  const { checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const paymentMethodShortName = checkoutState?.payment?.preferredPayment?.paymentMethodShortName;

  const calculatedBrowserInfo = useMemo<BrowserInfo>(() => {
    try {
      const { screen, navigator } = window ?? {};

      return {
        screenWidth: screen?.width ?? DEFAULT_BROWSER_INFO.screenWidth,
        screenHeight: screen?.height ?? DEFAULT_BROWSER_INFO.screenHeight,
        colorDepth: screen?.colorDepth ?? DEFAULT_BROWSER_INFO.colorDepth,
        userAgent: navigator?.userAgent ?? DEFAULT_BROWSER_INFO.userAgent,
        timeZoneOffset: new Date().getTimezoneOffset() ?? DEFAULT_BROWSER_INFO.timeZoneOffset,
        language: navigator?.language ?? DEFAULT_BROWSER_INFO.language,
      };
    } catch (error) {
      logError(error, {
        extra: {
          message: 'Error calculating browser info',
          paymentMethod: paymentMethodShortName,
        },
      });
      return DEFAULT_BROWSER_INFO;
    }
  }, []);

  useEffect(() => {
    try {
      if (
        paymentMethodShortName &&
        BROWSER_INFO_REQUIRED_PAYMENTS.includes(paymentMethodShortName)
      ) {
        setBrowserInfo(JSON.stringify(calculatedBrowserInfo));
      } else {
        setBrowserInfo('');
      }
    } catch (error) {
      logError(error, {
        extra: {
          message: 'Error setting browser info',
          paymentMethod: paymentMethodShortName,
        },
      });
      setBrowserInfo('');
    }
  }, [paymentMethodShortName, calculatedBrowserInfo]);

  return browserInfo;
};
